// @flow
import React from 'react'
import type {Node} from 'react'
import classNames from 'classnames'

import FormGroup from 'components/ui/FormGroup'
import type {Alert} from 'components/types'
import type {FakeOnKeyDownEvent} from 'components/ui/CKEditor'
import WYSIWYGTextareaWithAiSuggest from 'components/ai/AiSuggest/WYSIWYGTextareaWithAiSuggest'
import type {AiSuggestProps} from 'components/ai/AiSuggest/AiSuggestion'
import {useOrganizationContext} from 'contexts/OrganizationContext'
import AutocompleteInput from '../AutocompleteInput'
import type {Organization, Circle} from '../AutocompleteInput'
import type Value from '../Value'
import type {Path, UpdateCallback} from '../../../types'

type ValidationResult = {
  message?: string,
  className?: string,
  ...
}

type Props = $ReadOnly<{
  id: string,
  onChange: UpdateCallback,
  organization: Organization,
  path: Path,
  value: Value,
  alert?: ?Alert,
  autofocus?: boolean,
  circle?: ?Circle,
  className?: string,
  disabled?: boolean,
  isReadonly?: boolean,
  label?: Node,
  onKeyDown?: (event: SyntheticKeyboardEvent<HTMLTextAreaElement> | FakeOnKeyDownEvent) => void,
  onFocus?: () => void,
  rightButton?: Node,
  strikethrough?: boolean,
  textarea?: boolean,
  validate?: ?(value: string) => ?ValidationResult,
  placeholder?: string,
  mode?: 'inline' | 'classic',
  aiSuggestProps?: AiSuggestProps | void,
  containerClassName?: string,
}>

function ValueInput({
  disabled = false,
  isReadonly = false,
  label = null,
  strikethrough = false,
  textarea = false,
  autofocus = false,
  placeholder = I18n.t('proposals.rewrite.unspecified'),
  className = '',
  mode = 'inline',
  circle,
  alert,
  rightButton,
  value,
  containerClassName,
  ...props
}: Props, forwardedRef) {
  const {isChanged, isDeleted, isNew} = value

  const isDisabled = disabled || isDeleted || isReadonly

  const onChangeValue = (nextValue: string) => {
    value.setValue(nextValue)
    props.onChange(props.path, value)
  }

  const onChange = (event: Object) => {
    const nextText = event.target.value
    onChangeValue(nextText)
  }

  const inputClassName = classNames(
    className,
    'refinery-value-pill',
    'test-refinery-value-pill',
    {
      'refinery-value-pill--unchanged': !isChanged && !isNew,
      'refinery-value-pill--changed': isChanged,
      'refinery-value-pill--deleted': isDeleted,
      'refinery-value-pill--new': isNew,
    },
  )

  const textValue = isDeleted
    ? value.prevValue
    : value.nextValue

  const validationResult = props.validate
    ? props.validate(textValue) || {}
    : {}

  // if aiSuggestProps is passed, add `setValue` to it
  const aiSuggestProps = props.aiSuggestProps ? {
    context: props.aiSuggestProps.context,
    setValue: onChangeValue,
    promptKey: props.aiSuggestProps.promptKey,
    data: props.aiSuggestProps.data,
  } : undefined

  const organization = useOrganizationContext()

  return (
    <FormGroup
      alert={alert}
      className={validationResult.className}
      feedback={validationResult.message}
      label={label}
      rightButton={rightButton}
      target={props.id}
    >
      {textarea && (
        <WYSIWYGTextareaWithAiSuggest
          tinyMarginBottom
          mode={mode}
          hideLabel
          hideWarning
          autofocus={isNew && autofocus}
          value={textValue}
          isWysiwygEnabled={organization.isWysiwygEnabled}
          onChange={onChange}
          label={label || ''}
          name={props.id}
          organization={organization}
          onKeyDown={props.onKeyDown}
          onFocus={props.onFocus}
          disabled={isDisabled}
          innerRef={forwardedRef}
          warning={alert?.message || validationResult.message}
          warningStatus={alert ? 'error' : 'warning'}
          removed={!!isDeleted}
          updated={!!isChanged}
          rows={1}
          paddingSize="large"
          fallbackProps={{
            className: inputClassName,
            id: props.id,
            organization: props.organization,
            shouldFocus: isNew && autofocus,
            value: textValue,
            onChange,
            circle,
            strikethrough,
            textarea,
            placeholder,
          }}
          /* $FlowFixMe[incompatible-type] */
          fallbackComponent={AutocompleteInput}
          aiSuggestProps={aiSuggestProps}
        />
      )}
      {!textarea && (
        /* $FlowFixMe[incompatible-type-arg] */
        <AutocompleteInput
          circle={circle}
          className={inputClassName}
          containerClassName={containerClassName}
          disabled={isDisabled}
          id={props.id}
          onChange={onChange}
          onKeyDown={props.onKeyDown}
          onFocus={props.onFocus}
          organization={props.organization}
          shouldFocus={isNew && autofocus}
          strikethrough={strikethrough}
          textarea={textarea}
          value={textValue}
          placeholder={placeholder}
          aiSuggestProps={aiSuggestProps}
        />
      )}
    </FormGroup>
  )
}

export default (React.forwardRef<Props, _>(ValueInput): React$AbstractComponent<Props, mixed>)

export type {Alert, Circle, Organization, ValidationResult}
